import React, { useState, useEffect, useRef } from 'react';
import LoadingComponent from '@/components/common/LoadingComponent';
import ErrorState from '@/components/common/ErrorStateComponent';
import { apiCall } from '@/api/apiCall';
import { useQueue } from '@/context/QueueContext';
import BarChartWithTable from '@/components/BoardPack/components/charts/BarChartWithTable';

const KpiChartNew = ({ year = "", month = "", monthNumber = "", contract = "", page = null }) => {
  const { enqueue } = useQueue();

  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);

  /**
   * value_type logic:
   *  2 → Revenue          (plain numbers, has Base Rent)
   *  3 → Avg Rent Sq Mtr  (plain numbers, has Base Rent)
   *  4 → Occupancy        (bars = plain numbers, only Occupancy line = percent)
   *
   * API flat response:
   * {
   *   "data": {
   *     "Jan": { "Prior Year": "229", "Budget": "247", "Actual": "222", "Occupancy": "86%", "Base": "222" },
   *     ...
   *   }
   * }
   */
  const transformApiData = (apiResponse) => {
    const data = apiResponse?.data;
    if (!data || typeof data !== 'object') return null;

    const value_type = page?.params?.value_type;

    // Filter valid month keys
    const monthKeys = Object.keys(data).filter(key => {
      const val = data[key];
      return val && typeof val === 'object' && !Array.isArray(val);
    });

    if (monthKeys.length === 0) return null;

    // Helper: parse "1,774,051" or "88%" → number
    const parseValue = (value) => {
      if (value === undefined || value === null) return 0;
      if (typeof value === 'number') return value;
      return parseFloat(String(value).replace(/,/g, '').replace('%', '')) || 0;
    };

    const firstMonth = data[monthKeys[0]];

    const transformed = {
      labels:    monthKeys,
      priorYear: monthKeys.map(m => parseValue(data[m]['Prior Year'])),
      budget:    monthKeys.map(m => parseValue(data[m]['Budget'])),
      actual:    monthKeys.map(m => parseValue(data[m]['Actual'])),
      isPercent: false, // bars are always plain numbers regardless of value_type
    };

    // Base Rent → present for value_type 2 & 3 (key can be "Base Rent" or "Base")
    if ('Base Rent' in firstMonth || 'Base' in firstMonth) {
      transformed.baseRent = monthKeys.map(m =>
        parseValue(data[m]['Base Rent'] ?? data[m]['Base'])
      );
    }

    // Occupancy line → only for value_type 4
    // occupancy     = numeric values for the chart line
    // occupancyRaw  = original "88%" strings for the table display
    if (value_type === 4 && 'Occupancy' in firstMonth) {
      transformed.occupancy    = monthKeys.map(m => parseValue(data[m]['Occupancy']));
      transformed.occupancyRaw = monthKeys.map(m => data[m]['Occupancy'] ?? '');
    }

    return transformed;
  };

  useEffect(() => {
    // if (!year || !month || !contract) return;
    // if (hasFetchedRef.current) return;
    // hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = { ...(page?.params || {}) };
      Object.keys(params).forEach(key => {
        if (params[key] === '{{year}}')            params[key] = year;
        else if (params[key] === '{{month}}')       params[key] = month;
        else if (params[key] === '{{monthNumber}}') params[key] = monthNumber;
        else if (params[key] === '{{contract}}')    params[key] = contract;
      });

    //   const response = await enqueue(() => apiCall("GET", page?.api, params));
    const response = {
    "success": true,
    "message": "data fetched successfully",
    "server": "BI",
    "cube": "REAL_ESTATE_DATA_FOR_BI",
    "data": {
        "Jan": {
            "Prior Year": "1,774,051",
            "Budget": "2,118,473",
            "Actual": "1,862,811",
            "Base Rent": "1,862,811"
        },
        "Feb": {
            "Prior Year": "1,773,525",
            "Budget": "2,127,739",
            "Actual": "1,875,406",
            "Base Rent": "1,875,406"
        },
        "Mar": {
            "Prior Year": "1,798,386",
            "Budget": "2,146,006",
            "Actual": "1,905,480",
            "Base Rent": "1,905,480"
        },
        "Apr": {
            "Prior Year": "1,818,417",
            "Budget": "2,162,943",
            "Actual": "1,917,750",
            "Base Rent": "1,917,750"
        }
    }
};

      if (response?.success) {
        const transformed = transformApiData(response);
        if (transformed) {
          setChartData(transformed);
        } else {
          setError("No data available");
        }
      } else {
        setError(response?.message || "Failed to load data. Please try again later.");
      }
    } catch (err) {
      setError(err?.message || "Failed to load data. Please try again later.");
      console.error("Error fetching KPI chart data:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingComponent />;
  if (error)   return <ErrorState error={error} onRetry={fetchData} />;
  if (!chartData) return <ErrorState error="No data available" onRetry={fetchData} />;

  const value_type = page?.params?.value_type;

  // baseLines: for value_type 4, show occupancy as a line
  const baseLines = value_type === 4
    ? [{ key: 'occupancy', label: 'Occupancy', color: '#C90A0A', dashPattern: [6, 3] }]
    : [];

  // Dynamic strings — replace {{year}}, {{month}}, {{contract}}
  const replacePlaceholders = (str = '') =>
    str
      .replace(/{{year}}/g, year)
      .replace(/{{month}}/g, month)
      .replace(/{{contract}}/g, contract);

  const dynamicSubtitle       = replacePlaceholders(page?.subtitle);
  const dynamicInChartContent = replacePlaceholders(page?.in_chart_content_line_1);

  return (
    <BarChartWithTable
      title={page?.title || 'KPI Chart'}
      subtitle={dynamicSubtitle}
      inChartContent={dynamicInChartContent}
      data={chartData}
      chartHeight={page?.chartHeight || "400px"}
      showLegend={page?.showLegend !== false}
      showTable={page?.showTable !== false}
      baseLines={baseLines}
    />
  );
};

export default KpiChartNew;